// Proof of possession prevents copied tickets from working in another client.
// It does not attest official UI code or protect an already-compromised origin.
export const CLIENT_PROOF_VERSION = 'p256-sha256-v1';

function base64Url(bytes) {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function socketProofMessage(challenge, authentication) {
  const login = Boolean(authentication.loginTicket);
  return new TextEncoder().encode(JSON.stringify([
    'wanli-socket-proof-v1', challenge.releaseId, challenge.host, challenge.origin,
    challenge.socketId, challenge.nonce, String(challenge.expiresAt),
    login ? 'login' : 'reconnect', login ? authentication.loginTicket : authentication.reconnectTicket,
    authentication.clienttype
  ]));
}

export class BrowserSocketProof {
  constructor({ cryptoRef = globalThis.crypto, indexedDBRef = globalThis.indexedDB } = {}) {
    this.crypto = cryptoRef;
    this.indexedDB = indexedDBRef;
    this.key = null;
    this.publicKey = null;
    this.sessionId = '';
  }

  async prepare() {
    if (!this.crypto?.subtle) throw new Error('浏览器不支持安全连接验证，请使用 HTTPS 和新版浏览器');
    const pair = await this.crypto.subtle.generateKey({ name: 'ECDSA', namedCurve: 'P-256' }, false, ['sign', 'verify']);
    this.key = pair.privateKey; // extractable=false; never exported or sent
    const jwk = await this.crypto.subtle.exportKey('jwk', pair.publicKey);
    this.publicKey = { kty: jwk.kty, crv: jwk.crv, x: jwk.x, y: jwk.y };
    this.sessionId = '';
    return this.publicKey;
  }

  async store(sessionId) {
    if (!sessionId || !this.key || this.key.extractable) throw new Error('客户端连接验证密钥无效');
    await this.storage('readwrite', store => store.put({ key: this.key, publicKey: this.publicKey }, sessionId));
    this.sessionId = sessionId;
  }

  async restore(sessionId) {
    if (sessionId && this.sessionId === sessionId && this.key && !this.key.extractable) return;
    const saved = await this.storage('readonly', store => store.get(sessionId));
    if (!saved?.key || saved.key.extractable || saved.key.type !== 'private' || saved.key.algorithm?.name !== 'ECDSA' || saved.key.algorithm?.namedCurve !== 'P-256' || !saved.key.usages?.includes('sign')) {
      const error = new Error('本浏览器的连接验证密钥已丢失，请重新登录');
      error.code = 'CLIENT_PROOF_MISSING';
      error.sessionInvalid = true;
      throw error;
    }
    this.key = saved.key;
    this.publicKey = saved.publicKey;
    this.sessionId = sessionId;
  }

  async sign(challenge, authentication, { releaseId, hostname, socketId }) {
    if (!this.key || challenge.clientProof !== CLIENT_PROOF_VERSION || challenge.releaseId !== releaseId || challenge.host !== hostname || challenge.origin !== hostname || challenge.socketId !== socketId || typeof challenge.nonce !== 'string' || !/^[a-f0-9]{32}$/.test(challenge.nonce) || !Number.isSafeInteger(challenge.expiresAt) || (!authentication.loginTicket && !authentication.reconnectTicket)) {
      throw new Error('服务器连接挑战与当前客户端不匹配，请刷新页面');
    }
    const signature = await this.crypto.subtle.sign({ name: 'ECDSA', hash: 'SHA-256' }, this.key, socketProofMessage(challenge, authentication));
    return { version: CLIENT_PROOF_VERSION, signature: base64Url(new Uint8Array(signature)) };
  }

  async remove(sessionId) {
    if (!sessionId) return;
    await this.storage('readwrite', store => store.delete(sessionId));
    if (sessionId === this.sessionId) { this.key = null; this.publicKey = null; this.sessionId = ''; }
  }

  storage(mode, operation) {
    if (!this.indexedDB) return Promise.reject(new Error('浏览器安全存储不可用，无法保存连接验证密钥'));
    return new Promise((resolve, reject) => {
      const open = this.indexedDB.open('wanli-client-proof-v1', 1);
      open.onupgradeneeded = () => open.result.createObjectStore('sessions');
      open.onerror = () => reject(new Error('浏览器安全存储无法打开'));
      open.onblocked = () => reject(new Error('浏览器安全存储正在被占用，请关闭其它页面重试'));
      open.onsuccess = () => {
        const db = open.result;
        let result;
        try {
          const transaction = db.transaction('sessions', mode);
          const request = operation(transaction.objectStore('sessions'));
          request.onsuccess = () => { result = request.result; };
          transaction.oncomplete = () => { db.close(); resolve(result); };
          transaction.onabort = transaction.onerror = () => { db.close(); reject(new Error('浏览器连接验证密钥存储失败')); };
        } catch (error) { db.close(); reject(error); }
      };
    });
  }
}
